/**
 * Single Accordion
 *
 * @module
 */

import { up } from '../../../utilities/QuerySelectors.js';
import { mediaQuery as isMediaQuerySupported } from '../../../utilities/FeatureSupportChecks.js';

export class SingleAccordion {
    /**
     * Accordion component to make a sinle element collapsible.
     *
     * @param {HTMLElement} toggler
     * @param {HTMLElement} content
     * @param {string} [isEnabledMediaQuery] Media query specifying when the accordion should be enabled.
     */
    constructor(toggler, content, isEnabledMediaQuery = '') {
        this.toggler = toggler;
        this.content = content;
        this.isEnabledMediaQuery = isEnabledMediaQuery;
        this.isToggleOnIconOnlyEnabled = false;
    }

    /**
     * Only toggle the accordion when the user clicks on the +- icon.
     *
     * @private
     * @return {void}
     */
    toggleOnIconOnly() {
        this.isToggleOnIconOnlyEnabled = true;
    }

    /**
     * Display the content.
     *
     * @private
     * @return {void}
     */
    expand() {
        this.toggler.classList.add('expanded');
        this.content.classList.add('expanded');
        this.container.classList.add('expanded');
        this.isExpanded = true;
    }

    /**
     * Hide the content.
     *
     * @private
     * @return {void}
     */
    collapse() {
        this.toggler.classList.remove('expanded');
        this.content.classList.remove('expanded');
        this.container.classList.remove('expanded');
        this.isExpanded = false;
    }

    /**
     * Toggle the accordion if it's a valid click.
     *
     * @private
     * @return {void}
     */
    onTogglerClick(e) {
        if (this.isToggleOnIconOnlyEnabled && !e.target.classList.contains('accordion-icon')) {
            return;
        }
        this.toggle();
    }

    /**
     * Invert the accordion display state.
     *
     * @private
     * @return {void}
     */
    toggle() {
        if (this.isExpanded) {
            return this.collapse();
        }
        this.expand();
    }

    /**
     * Enable toggling on toggler click.
     *
     * @private
     * @return {void}
     */
    enable() {
        this.container.classList.add('enabled');
    }

    /**
     * Disable toggling.
     *
     * @private
     * @return {void}
     */
    disable() {
        this.container.classList.remove('enabled');
    }

    /**
     * Initial classes for UI sync of toggling state.
     *
     * @private
     * @return {void}
     */
    addClasses() {
        this.container.classList.add('single-accordion');
        this.toggler.classList.add('single-accordion__toggler');
        this.content.classList.add('single-accordion__content');
    }

    /**
     * Sync operational status with matching of media query.
     *
     * @private
     * @return {void}
     */
    initStatusMediaQuery() {
        if (!this.isEnabledMediaQuery || !isMediaQuerySupported()) {
            this.enable();
            return;
        }
        const mql = window.matchMedia(this.isEnabledMediaQuery);
        mql.addEventListener('change', (e) => (e.matches ? this.enable() : this.disable()));
        mql.matches ? this.enable() : this.disable();
    }

    /**
     * Initialize the accordion features.
     *
     * 1. Sync operational status with optionally given media query.
     * 2. Add css classes for UI state sync.
     * 3. Set initial display state.
     * 4. Add + and - icons to indicate state.
     * 5. Add toggler click event handler.
     *
     * @return {void}
     */
    init() {
        if (!this.toggler) {
            return;
        }

        this.container = up(this.toggler, '.single-accordion');

        if (!this.container) {
            this.container = this.toggler.parentElement;
        }

        this.initStatusMediaQuery();
        this.addClasses();

        // Toggle initial expansion state on init
        const isVisible = window.getComputedStyle(this.content).getPropertyValue('visibility') !== 'hidden';
        const alwaysExpandsOnPageLoad = this.container.getAttribute('data-always-expands-on-page-load') === 'true';
        this.isExpanded = isVisible || alwaysExpandsOnPageLoad;
        this.isExpanded ? this.expand() : this.collapse();

        this.toggler.insertAdjacentHTML(
            'beforeend',
            `
            <span class="dashicons accordion-icon dashicons-plus-alt2"></span>
            <span class="dashicons accordion-icon dashicons-minus"></span>
        `,
        );

        this.toggler.addEventListener('click', (e) => this.onTogglerClick(e));
    }
}
